import { FC, useState } from 'react'
import {
  PageHeader,
  EmptyState,
  PermissionIcon,
  Button,
  Icon,
} from '@lmc/ui-kit'
import { ResidentStatus } from '@lmc/ts-common'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import styled from 'styled-components'
import { useRouter } from 'hooks/useRouter'
import useResident from 'hooks/useResident'
import CernerHIE from './CernerHIE'
import { useSelector } from 'react-redux'
import { User, Home, ResidentItem } from '@lmc/ts-common'
import { ButtonsWrapper } from '../../styledComponents'
import { cernerTranslations } from 'locale/cerner'

type Props = {}

type ValidationCheckProps = {
  resident: ResidentItem
  hasSharedCareRecordEnabled: boolean | undefined
  hasSharedCareRecordPermission: boolean | undefined
  children: React.ReactNode
}

const ValidationCheck: FC<ValidationCheckProps> = ({
  resident,
  hasSharedCareRecordEnabled,
  hasSharedCareRecordPermission,
  children,
}) => {
  const { t } = useTranslation(NAMESPACE.CERNER)

  // Check for Clinical Role
  if (!hasSharedCareRecordPermission) {
    return (
      <EmptyState
        title={t(cernerTranslations.PERMISSION_REQUIRED)}
        icon={<PermissionIcon />}
        description={t(cernerTranslations.PERMISSION_DESCRIPTION)}
      />
    )
  }

  // Check for ODS Code
  if (!hasSharedCareRecordEnabled) {
    return (
      <EmptyState
        title={t(cernerTranslations.SITE_CONFIGURATION_REQUIRED)}
        icon={<PermissionIcon />}
        description={t(cernerTranslations.SITE_CONFIGURATION_DESCRIPTION)}
      />
    )
  }

  // Check for Active Service User
  if (resident.status !== ResidentStatus.active) {
    return (
      <EmptyState
        title={t(cernerTranslations.INACTIVE_SERVICE_USER)}
        icon={<PermissionIcon />}
        description={t(cernerTranslations.INACTIVE_SERVICE_USER_DESCRIPTION)}
      />
    )
  }

  // Check for NHS Number
  if (!resident.nhsNumber) {
    return (
      <EmptyState
        title={t(cernerTranslations.NHS_NUMBER_REQUIRED)}
        icon={<PermissionIcon />}
        description={t(cernerTranslations.NHS_NUMBER_DESCRIPTION)}
      />
    )
  }

  return children
}

const CollapseButton = styled(Button)`
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 1001;
  background-color: white;
  display: flex;
  align-items: center;
  gap: 8px;
`

export const SharedCareRecordScreen: FC<Props> = () => {
  const { t } = useTranslation(NAMESPACE.CERNER)
  const {
    params: { id },
  } = useRouter()

  const { resident } = useResident({ id })

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const user = useSelector(
    (state: { global: { user: User } }) => state.global.user
  )

  const hasSharedCareRecordEnabled = !!home?.odsCode
  const hasSharedCareRecordPermission = !!user?.gpConnectPermission

  const [isFullScreen, setIsFullScreen] = useState(false)

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen)
  }

  if (!resident) {
    return 'Loading...'
  }

  return (
    <MainContainer>
      <PageHeader
        sticky
        title={`${resident?.name.first} ${resident?.name.last}'s Shared Care Record`}
      >
        <ButtonsWrapper>
          <Button
            type="button"
            onClick={toggleFullScreen}
            style={{ backgroundColor: 'white' }}
          >
            <Icon
              name={
                isFullScreen ? 'FullscreenExitOutlined' : 'FullscreenOutlined'
              }
            />
            {isFullScreen
              ? t(cernerTranslations.COLLAPSE)
              : t(cernerTranslations.EXPAND)}
          </Button>
        </ButtonsWrapper>
      </PageHeader>
      <SCRContainer className={isFullScreen ? 'full-screen' : ''}>
        {isFullScreen && (
          <CollapseButton type="button" onClick={toggleFullScreen}>
            <Icon name="FullscreenExitOutlined" />
            {t(cernerTranslations.COLLAPSE)}
          </CollapseButton>
        )}
        <ValidationCheck
          resident={resident}
          hasSharedCareRecordEnabled={hasSharedCareRecordEnabled}
          hasSharedCareRecordPermission={hasSharedCareRecordPermission}
        >
          <IframeWrapper className={isFullScreen ? 'full-screen' : ''}>
            <CernerHIE residentId={resident.id} />
          </IframeWrapper>
        </ValidationCheck>
      </SCRContainer>
    </MainContainer>
  )
}

const MainContainer = styled.div`
  max-width: 1080px;
  margin: 24px auto;
  min-width: 1024px; // NHSx requirement
  min-height: 768px; // NHSx requirement
`

const SCRContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 24px;
  margin-bottom: 20px;
  &.full-screen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: white;
    z-index: 1000;
    margin: 0;
    padding: 0;
  }
`

const IframeWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 80vh;
  &.full-screen {
    height: 100vh;
    width: 100vw;
    iframe {
      width: 100%;
      height: 100%;
    }
  }
`
